import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, Loading, Pill } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Building2, Map, Network, Users, Boxes, Factory, Warehouse, MapPin, Store, Ruler,
  Plus, Pencil, Trash2,
} from "lucide-react";

const MASTERS = [
  { key: "opcos", label: "Operating Companies", icon: Building2 },
  { key: "regions", label: "Regions", icon: Map },
  { key: "rsos", label: "RSOs", icon: Network },
  { key: "aos", label: "Area Offices", icon: Network },
  { key: "states", label: "States", icon: MapPin },
  { key: "cities", label: "Cities", icon: MapPin },
  { key: "plants", label: "Plants", icon: Factory },
  { key: "warehouses", label: "Warehouses", icon: Warehouse },
  { key: "dealers", label: "Dealers", icon: Users },
  { key: "retailers", label: "Retailers", icon: Store },
  { key: "skus", label: "SKUs", icon: Boxes },
  { key: "uom_master", label: "UoM Master", icon: Ruler },
];

const WRITE_ROLES = ["Super Administrator", "Corporate Scheme Admin", "OpCo Scheme Manager", "Data Operations"];
const HIDE = new Set(["id", "opco_id", "region_id", "rso_id", "ao_id", "city_id", "state_id", "dealer_id", "sku_category"]);

// Editable field specs per collection. rel => dropdown from related collection.
const FIELD_SPECS = {
  opcos: [{ key: "code" }, { key: "name" }, { key: "company" }],
  regions: [{ key: "name" }, { key: "opco_id", rel: "opcos" }],
  rsos: [{ key: "name" }, { key: "opco_id", rel: "opcos" }, { key: "region_id", rel: "regions" }],
  aos: [{ key: "name" }, { key: "opco_id", rel: "opcos" }, { key: "region_id", rel: "regions" }, { key: "rso_id", rel: "rsos" }],
  states: [{ key: "name" }, { key: "code" }],
  cities: [{ key: "name" }, { key: "state_id", rel: "states" }],
  plants: [{ key: "name" }, { key: "opco_id", rel: "opcos" }, { key: "capacity_mtpa", type: "number" }],
  warehouses: [{ key: "name" }, { key: "opco_id", rel: "opcos" }],
  dealers: [{ key: "code" }, { key: "name" }, { key: "status", type: "status" },
            { key: "opco_id", rel: "opcos" }, { key: "region_id", rel: "regions" },
            { key: "rso_id", rel: "rsos" }, { key: "ao_id", rel: "aos" }],
  retailers: [{ key: "code" }, { key: "name" }, { key: "dealer_id", rel: "dealers" }],
  skus: [{ key: "code" }, { key: "name" }, { key: "category" }, { key: "bags_per_mt", type: "number" }, { key: "price_per_mt", type: "number" }],
  uom_master: [{ key: "sku_category" }, { key: "bags_per_mt", type: "number" }, { key: "bag_kg", type: "number" }],
};

const relLabel = (c, x) => (c === "opcos" || c === "dealers") ? `${x.code} — ${x.name}` : (x.name || x.code || x.id);

export default function MasterData() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [active, setActive] = useState("dealers");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["master", active],
    queryFn: async () => (await api.get(`/master/${active}`)).data,
  });
  const { data: rels } = useQuery({
    queryKey: ["master-rels"],
    queryFn: async () => {
      const cs = ["opcos", "regions", "rsos", "aos", "states", "dealers"];
      const out = {};
      await Promise.all(cs.map(async (c) => { out[c] = (await api.get(`/master/${c}`)).data.items; }));
      return out;
    },
  });

  const cols = data?.items?.length ? Object.keys(data.items[0]).filter((c) => !HIDE.has(c)) : [];
  const spec = FIELD_SPECS[active] || [];

  const saveMut = useMutation({
    mutationFn: async ({ mode, record }) =>
      mode === "add" ? (await api.post(`/master/${active}`, record)).data
                     : (await api.put(`/master/${active}/${record.id}`, record)).data,
    onSuccess: () => { toast.success("Saved"); setEditing(null); qc.invalidateQueries({ queryKey: ["master", active] }); qc.invalidateQueries({ queryKey: ["master-rels"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/master/${active}/${id}`)).data,
    onSuccess: () => { toast.success("Deleted"); setToDelete(null); qc.invalidateQueries({ queryKey: ["master", active] }); },
    onError: (e) => { toast.error(e.response?.data?.detail || "Delete failed"); setToDelete(null); },
  });

  const openAdd = () => setEditing({ mode: "add", record: Object.fromEntries(spec.map((s) => [s.key, s.type === "number" ? 0 : ""])) });
  const openEdit = (row) => setEditing({ mode: "edit", record: { ...row } });
  const setField = (k, val) => setEditing((e) => ({ ...e, record: { ...e.record, [k]: val } }));

  return (
    <div>
      <PageHeader title="Master Data Designer" subtitle="Add, modify or remove records across all org, geography, fulfilment and product masters" />
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-2 h-fit">
          {MASTERS.map((m) => {
            const Icon = m.icon;
            return (
              <button key={m.key} data-testid={`master-tab-${m.key}`} onClick={() => setActive(m.key)}
                className={`nav-item w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium ${active === m.key ? "bg-[#0F2C59] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                <Icon size={16} /> {m.label}
              </button>
            );
          })}
        </div>

        <Panel testid="master-table-panel" title={MASTERS.find((m) => m.key === active)?.label}
          subtitle={data ? `${num(data.count)} records` : ""}
          actions={canWrite && (
            <Button size="sm" className="gap-2 bg-[#0F2C59] hover:bg-[#00529B]" data-testid="master-add-btn" onClick={openAdd}>
              <Plus size={15} /> Add Record
            </Button>
          )}>
          {isLoading || !data ? <Loading /> : (
            <div className="overflow-x-auto max-h-[65vh]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                    {cols.map((c) => <th key={c} className="py-2.5 pr-4 whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}
                    {canWrite && <th className="py-2.5 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.items.slice(0, 200).map((row, i) => (
                    <tr key={row.id || i} className="drill-row border-b border-slate-50" data-testid={`master-row-${i}`}>
                      {cols.map((c) => (
                        <td key={c} className="py-2.5 pr-4 whitespace-nowrap text-slate-700">
                          {c === "status" ? <Pill tone={row[c]}>{row[c]}</Pill>
                            : typeof row[c] === "object" && row[c] !== null ? JSON.stringify(row[c])
                            : String(row[c] ?? "—")}
                        </td>
                      ))}
                      {canWrite && (
                        <td className="py-2.5 text-right whitespace-nowrap">
                          <div className="flex justify-end gap-1.5">
                            <Button size="sm" variant="outline" className="h-8 px-2" data-testid={`master-edit-${i}`} onClick={() => openEdit(row)}>
                              <Pencil size={14} />
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 px-2 text-red-600 border-red-200" data-testid={`master-delete-${i}`} onClick={() => setToDelete(row)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.count > 200 && <div className="text-xs text-slate-400 mt-3">Showing first 200 of {num(data.count)} records.</div>}
            </div>
          )}
        </Panel>
      </div>

      {/* Add / Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white max-h-[85vh] overflow-y-auto" data-testid="master-form-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing?.mode === "add" ? "Add" : "Edit"} {MASTERS.find((m) => m.key === active)?.label}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              {spec.map((f) => (
                <div key={f.key}>
                  <Label className="text-slate-700 text-sm capitalize">{f.key.replace(/_/g, " ").replace(" id", "")}</Label>
                  <div className="mt-1.5">
                    {f.rel ? (
                      <select className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                        data-testid={`master-field-${f.key}`} value={editing.record[f.key] || ""}
                        onChange={(e) => setField(f.key, e.target.value)}>
                        <option value="">Select…</option>
                        {(rels?.[f.rel] || []).map((x) => <option key={x.id} value={x.id}>{relLabel(f.rel, x)}</option>)}
                      </select>
                    ) : f.type === "status" ? (
                      <select className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                        data-testid={`master-field-${f.key}`} value={editing.record[f.key] || "active"}
                        onChange={(e) => setField(f.key, e.target.value)}>
                        {["active", "new", "revival", "inactive"].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <Input type={f.type === "number" ? "number" : "text"} data-testid={`master-field-${f.key}`}
                        value={editing.record[f.key] ?? ""}
                        onChange={(e) => setField(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)} />
                    )}
                  </div>
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} data-testid="master-form-cancel">Cancel</Button>
                <Button className="bg-[#0F2C59] hover:bg-[#00529B]" data-testid="master-form-save"
                  disabled={saveMut.isPending} onClick={() => saveMut.mutate(editing)}>Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white" data-testid="master-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.name || toDelete?.code || "This record"} will be permanently removed from {active}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="master-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="master-delete-confirm"
              onClick={() => delMut.mutate(toDelete.id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
